import React from 'react';
import { SchematicShell, Circle, Line, Rect } from './SchematicPrimitives';
import {
  FAN_HUB_RADIUS_FRAC,
  FAN_RING_TUBE_FRAC,
  FAN_SLAT_COUNT,
  OAR_BAND_RADIUS_SCALE,
  OAR_BAND_WIDTH_FRAC,
  OAR_BLADE_BEZIER_OVERSHOOT,
  OAR_BLADE_LENGTH_FRAC,
  OAR_GRIP_HEIGHT_FRAC,
  OAR_GRIP_LENGTH_FRAC,
  OAR_SHAFT_RADIUS_FRAC,
} from '../../3d/geometry/proceduralRenderers/proceduralSpecs';

/**
 * Coastal wall decor, 2D. Both proportions come from `proceduralSpecs.js` —
 * the same constants the 3D renderers use — because these are the "drawn
 * twice" case web-app/CLAUDE.md warns about.
 *
 * Without these the elevation fell through to `getSchematicComponent`'s null
 * and drew a plain rectangle: a 28" rattan CIRCLE rendered as a square, and a
 * 54" oar as a bar. That is exactly the defect the dorm decor elevations were
 * added to fix ("as rectangles they were indistinguishable from each other and
 * from the wall").
 *
 * SVG space is meters with +y DOWN, so the 3D renderer's centreline y = 0
 * becomes y = height / 2 here.
 */
export function DormWallOarSchematic({
  widthMeters: width,
  heightMeters: height,
  strokeColor,
  strokeWidth,
}) {
  const mid = height / 2;
  const bladeLen = width * OAR_BLADE_LENGTH_FRAC;
  const gripLen = width * OAR_GRIP_LENGTH_FRAC;
  const shaftLen = width - bladeLen - gripLen;
  const shaftR = Math.min(height * OAR_SHAFT_RADIUS_FRAC, height) / 2;
  const bandW = shaftLen * OAR_BAND_WIDTH_FRAC;
  const gripH = height * OAR_GRIP_HEIGHT_FRAC;

  // The blade's bezier controls overshoot the target width (they peak ~3/4 of
  // the way), so the same factor the 3D shape uses has to be applied here or
  // the 2D blade comes out visibly narrower than the object it depicts.
  const peak = mid * OAR_BLADE_BEZIER_OVERSHOOT;
  const blade =
    `M 0 ${mid}` +
    ` C ${bladeLen * 0.2} ${mid - peak} ${bladeLen * 0.7} ${mid - peak} ${bladeLen} ${mid}` +
    ` C ${bladeLen * 0.7} ${mid + peak} ${bladeLen * 0.2} ${mid + peak} 0 ${mid}`;

  return (
    <SchematicShell
      width={width}
      height={height}
      strokeColor={strokeColor}
      strokeWidth={strokeWidth}
      drawFrame={false}
    >
      <path
        d={blade}
        fill="none"
        stroke={strokeColor}
        strokeWidth={strokeWidth}
        strokeLinejoin="round"
        vectorEffect="non-scaling-stroke"
      />
      <Rect
        x={bladeLen}
        y={mid - shaftR}
        width={shaftLen}
        height={shaftR * 2}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth}
      />
      {/* Painted band where blade meets shaft — the detail that makes it read
          as decorative rather than as a tool. */}
      <Rect
        x={bladeLen + bandW / 2}
        y={mid - shaftR * OAR_BAND_RADIUS_SCALE}
        width={bandW}
        height={shaftR * OAR_BAND_RADIUS_SCALE * 2}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth}
      />
      <Rect
        x={width - gripLen}
        y={mid - gripH / 2}
        width={gripLen}
        height={gripH}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth}
      />
    </SchematicShell>
  );
}

export function DormRattanFanSchematic({
  widthMeters: width,
  heightMeters: height,
  strokeColor,
  strokeWidth,
}) {
  const cx = width / 2;
  const cy = height / 2;
  const radius = Math.min(width, height) / 2;
  const tube = radius * FAN_RING_TUBE_FRAC;
  const outer = radius - tube;
  const hub = radius * FAN_HUB_RADIUS_FRAC;

  return (
    <SchematicShell
      width={width}
      height={height}
      strokeColor={strokeColor}
      strokeWidth={strokeWidth}
      drawFrame={false}
    >
      <Circle
        cx={cx}
        cy={cy}
        r={outer}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth}
      />
      <Circle
        cx={cx}
        cy={cy}
        r={hub}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth}
      />
      {Array.from({ length: FAN_SLAT_COUNT }, (_, i) => {
        const a = (i / FAN_SLAT_COUNT) * Math.PI * 2;
        return (
          <Line
            key={i}
            x1={cx + Math.cos(a) * hub}
            y1={cy + Math.sin(a) * hub}
            x2={cx + Math.cos(a) * outer}
            y2={cy + Math.sin(a) * outer}
            strokeColor={strokeColor}
            strokeWidth={strokeWidth * 0.5}
          />
        );
      })}
    </SchematicShell>
  );
}
